import {
  addClass,
  removeClass,
  getTallestElement,
  matchHeight,
  shrinkAccordionItems,
  toggleFloatPanels,
  toggleAccordionItem,
} from './common-functions';
import { debounce } from 'lodash';

const { feWp } = window;
const { applyFilters } = feWp.hooks;

const constants = require('@constants');
const defaultCssClass = constants.DEFAULT_CSS_CLASS.ACCORDION;
const clzFloatItemsWrapper = `.${defaultCssClass}__flt-itms-wrpr`;
const clzFloatItemWrapper = `.${defaultCssClass}__flt-itm-wrpr`;
const clzFloatItem = `.${defaultCssClass}__flt-itm`;
const clzItems = `.${defaultCssClass}__itm-wrpr`;
const clzItemBody = `.${defaultCssClass}__itm-bdy`;
const activeClass = 'active';
const initClass = 'initialized';

export const appendPanel = (
  acc,
  items,
  floatPanelsWrapper,
  floatPannels,
  settings,
  mobileView,
) => {
  const { autoSlide } = settings;

  if (mobileView) {
    items.forEach((el) => {
      floatPannels.forEach((flEl) => {
        const id = flEl.dataset.id;
        if (
          el.querySelector('[data-toggle="collapse"]').dataset.target === id
        ) {
          el.querySelector(clzItemBody).after(flEl);
          removeClass(acc, 'column');
          if (autoSlide) removeClass(acc, 'auto-slide');
        }
      });
    });
  } else {
    floatPannels.forEach((flEl) => {
      floatPanelsWrapper.appendChild(flEl);
      addClass(acc, 'column');
      if (autoSlide) addClass(acc, 'auto-slide');
    });
  }
};

const floatPanelWrapperSetHeight = (floatPanelsWrapper, floatPannelInner) => {
  const height = getTallestElement(floatPannelInner);
  floatPanelsWrapper.style.minHeight = height + 'px';
};

export const toggleAccordion = (
  handles,
  settings,
  floatPannels,
  items,
  smoothSlide,
) => {
  const { toggleMode, floatPanel } = settings;

  handles.forEach((handle) => {
    handle.addEventListener('click', (el) => {
      const id = el.currentTarget.dataset.target;
      const filterActiveItems = items.filter(
        (i) => i.classList.contains(activeClass) && i.dataset.itemId !== id,
      );
      if (toggleMode) {
        shrinkAccordionItems(filterActiveItems, smoothSlide);
      }
      toggleAccordionItem(id, smoothSlide);
      if (floatPanel) {
        toggleFloatPanels(floatPannels, id);
      }
    });
  });
};

export const autoPlayAccordion = (
  accordions,
  settings,
  slides,
  items,
  smoothSlide = false,
) => {
  const state = {
    set: null,
    count: getInitialSlideIndex(slides),
    areaFocus: false,
    autoplayEnabled: true,
  };

  const { autoPlayInterval, colBreakPoint, pauseOnFocus } = settings;
  const interval = autoPlayInterval || 3000;
  const slidesCount = slides.length;

  const disableAutoPlayMobile = () =>
    window.matchMedia(`(max-width: ${colBreakPoint}px)`).matches;

  const initialize = () => {
    if (!state.autoplayEnabled || disableAutoPlayMobile()) return;

    accordions.classList.remove('autoplay-stopped');
    state.set = setInterval(next, interval);
  };

  const next = () => {
    clearInterval(state.set);
    updateSlides(slides, state.count, slidesCount, smoothSlide);
    state.count = (state.count + 1) % slidesCount;
    initialize();
  };

  const stopAutoplay = () => {
    clearInterval(state.set);
    state.autoplayEnabled = false;
    accordions.classList.add('autoplay-stopped');
  };

  const handleClick = (targetId) => {
    if (disableAutoPlayMobile()) return;

    stopAutoplay();
    updateOnClick(slides, state, targetId);

    if (!state.areaFocus) {
      initialize();
    }
  };

  setupEventListeners(accordions, state, pauseOnFocus, initialize);
  setupItemClickHandlers(items, handleClick);
  initialize();
};

const getInitialSlideIndex = (slides) => {
  const activeSlide = slides.filter((f) => f.classList.contains('active'));
  return slides.findIndex(
    (item) => item.dataset.id === activeSlide[0]?.dataset.id,
  );
};

const handleFadeIn = (el) => {
  el.style.opacity = '1';
  addClass(el);
};

const handleFadeOut = (el) => {
  el.style.opacity = '0';
  removeClass(el);
};

const updateSlides = (slides, currentCount, slidesCount, smoothSlide) => {
  handleFadeOut(slides[currentCount]);
  toggleAccordionItem(slides[currentCount].dataset.id, smoothSlide);
  const nextCount = (currentCount + 1) % slidesCount;
  handleFadeIn(slides[nextCount]);
  toggleAccordionItem(slides[nextCount].dataset.id, smoothSlide);
};

const toggleHandleClass = (id) => {
  if (!id) return;
  const items = document.querySelectorAll(clzItems);
  items.forEach(removeClass);
  const targetItem = Array.from(items).find(
    (item) => item.dataset.itemId === id,
  );
  if (targetItem) {
    addClass(targetItem, activeClass);
  }
};

const updateOnClick = (slides, state, targetId) => {
  state.count = slides.findIndex((id) => id.dataset.id === targetId);
  slides.forEach(handleFadeOut);
  handleFadeIn(slides[state.count]);
  toggleHandleClass(targetId);
};

const setupEventListeners = (accordions, state, pauseOnFocus, initialize) => {
  if (!pauseOnFocus) return;

  accordions.addEventListener('mouseenter', () => {
    clearInterval(state.set);
    state.areaFocus = true;
  });

  accordions.addEventListener('mouseleave', () => {
    state.areaFocus = false;
    if (state.autoplayEnabled) {
      initialize();
    }
  });
};

const setupItemClickHandlers = (items, handleClick) => {
  items.forEach((item) => {
    item.addEventListener('click', () => {
      const targetId = item.dataset.itemId;
      if (targetId) handleClick(targetId);
    });
  });
};

const setMatchHeight = (acc) => {
  matchHeight(acc.querySelectorAll(clzItemBody));
};

const extractAccordions = () => {
  const accordions = document.querySelectorAll('[data-accordion]');
  if (accordions.length <= 0) return;

  accordions.forEach((acc) => {
    if (acc.classList.contains(initClass)) return;
    acc.classList.add(initClass);

    const hasSmooth = acc.classList.contains('smooth-slide');
    const settings = JSON.parse(acc.dataset.settings);
    const items = Array.from(acc.querySelectorAll(clzItems));
    const floatPanelsWrapper = acc.querySelector(clzFloatItemsWrapper);
    const floatPannels = Array.from(acc.querySelectorAll(clzFloatItemWrapper));
    const floatPannelInner = Array.from(acc.querySelectorAll(clzFloatItem));
    const handles = Array.from(acc.querySelectorAll('[data-target]'));
    const mobileView = window.matchMedia(
      `(max-width: ${settings.colBreakPoint}px)`,
    ).matches;

    if (settings.autoPlayInterval) {
      const speed = parseInt(settings.autoPlayInterval, 10);
      acc.style.setProperty('--autoplay-speed', `${speed}ms`);
    }

    if (settings.floatPanel) {
      appendPanel(
        acc,
        items,
        floatPanelsWrapper,
        floatPannels,
        settings,
        mobileView,
      );
      floatPanelWrapperSetHeight(floatPanelsWrapper, floatPannelInner);
    }
    if (settings.autoSlide && !mobileView) {
      autoPlayAccordion(acc, settings, floatPannels, items, hasSmooth);
    }
    toggleAccordion(handles, settings, floatPannels, items, hasSmooth);
    setMatchHeight(acc);
  });
};

let currentWidth = window.innerWidth;
const init = applyFilters('b3rg_accordion_filter', () => {
  const debounceSetTab = debounce(() => {
    if (window.innerWidth !== currentWidth) {
      currentWidth = window.innerWidth;
      extractAccordions();
    }
  }, 600);

  window.addEventListener('load', () => {
    extractAccordions();
    window.addEventListener('resize', debounceSetTab);
  });
});

if (typeof init === 'function') {
  init();
}
